import { useNode } from "@craftjs/core";
import React from "react";

import {
  ColorInput,
  UnitInput,
  BoxShadowInput,
  PaddingInput,
  Panel,
  Tabs,
  TextInput,
  BorderRadiusInput,
  BorderInput,
  ToggleInput,
  Spacer,
  MarginInput,
  AlignInput,
  NumberInput,
  DateInput,
} from "../../input";
import { isUpdatedFromDefault } from "../../utils";

import { supportLibrary } from "../../block-supports";
import { INPUT_LIMITS } from "../../input-limits";

export const FormDateSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    width,
    height,
    align,
    alignTablet,
    alignMobile,
    layoutMargin,
    layoutMarginTablet,
    layoutMarginMobile,
    layoutPadding,
    layoutPaddingTablet,
    layoutPaddingMobile,
    inputMargin,
    inputMarginTablet,
    inputMarginMobile,
    inputPadding,
    inputPaddingTablet,
    inputPaddingMobile,
    typoColor,
    typoBackgroundColor,
    containerBackgroundColor,
    typoSize,
    inputBorder,
    layoutBorder,
    layoutBorderRadius,
    inputBorderRadius,
    layoutBoxShadow,
    inputBoxShadow,
    value,
    max,
    min,
    step,
    name,
    required,
    pattern,
    showLabel,
    label,
    labelColor,
    labelRequiredColor,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing={"1rem"}>
          <TextInput
            minLength={INPUT_LIMITS["form-input-name"].minlength}
            maxLength={INPUT_LIMITS["form-input-name"].maxlength}
            resetValue={undefined}
            value={name}
            onChange={(val) => {
              setProp((props) => {
                props.name = val;
              });
            }}
            label={"Name"}
          />
          <TextInput
            minLength={INPUT_LIMITS["form-date-pattern"].minlength}
            maxLength={INPUT_LIMITS["form-date-pattern"].maxlength}
            resetValue={undefined}
            value={pattern}
            onChange={(val) => {
              setProp((props) => {
                props.pattern = val;
              });
            }}
            label={"Pattern"}
          />
          <ToggleInput
            resetValue={undefined}
            value={required}
            onChange={(val) => {
              setProp((props) => {
                props.required = val;
              });
            }}
            label={"Required"}
          />
          <DateInput
            resetValue={undefined}
            value={min}
            onChange={(val) => {
              setProp((props) => {
                props.min = val;
              });
            }}
            label={"Min"}
          />
          <DateInput
            resetValue={undefined}
            value={max}
            onChange={(val) => {
              setProp((props) => {
                props.max = val;
              });
            }}
            label={"Max"}
          />
          {/* <NumberInput
            resetValue={undefined}
            value={step}
            onChange={(val) => {
              setProp((props) => {
                props.step = val;
              });
            }}
            label={"Step"}
            max={INPUT_LIMITS["form-date"].max}
            min={INPUT_LIMITS["form-date"].min}
            step={INPUT_LIMITS["form-date"].step}
          /> */}
          <DateInput
            resetValue={undefined}
            value={value}
            onChange={(val) => {
              setProp((props) => {
                props.value = val;
              });
            }}
            label={"Value"}
          />
          <UnitInput
            resetValue={undefined}
            value={width}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            resetValue={undefined}
            value={height}
            onChange={(val) => {
              setProp((props) => {
                props.height = val;
              });
            }}
            label={"Height"}
          />
          <UnitInput
            value={typoSize}
            onChange={(val) => {
              setProp((props) => {
                props.typoSize = val;
              });
            }}
            label={"Font Size"}
          />
          <AlignInput
            isResponsive={true}
            resetValue={undefined}
            valueTablet={alignTablet}
            valueMobile={alignMobile}
            value={align}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignMobile = val;
              });
            }}
            label={"Align"}
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: showLabel }, { key: label }])}
          id={`block-setting-${id}-label`}
          title={"Label"}
          initialOpen={false}
        >
          <ToggleInput
            resetValue={undefined}
            value={showLabel}
            onChange={(val) => {
              setProp((props) => {
                props.showLabel = val;
              });
            }}
            label={"Show Label"}
          />
          {showLabel && (
            <>
              <TextInput
                minLength={INPUT_LIMITS["form-input-label"].minlength}
                maxLength={INPUT_LIMITS["form-input-label"].maxlength}
                resetValue={undefined}
                value={label}
                onChange={(val) => {
                  setProp((props) => {
                    props.label = val;
                  });
                }}
                label={"Label"}
              />
            </>
          )}
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Spacer spacing={"1rem"}>
            <ColorInput
              value={typoColor}
              onChange={(val) => {
                setProp((props) => {
                  props.typoColor = val;
                });
              }}
              label={"Color"}
            />
            <ColorInput
              value={typoBackgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.typoBackgroundColor = val;
                });
              }}
              label={"Background"}
            />
          </Spacer>
          {showLabel && (
            <Panel
              isUpdated={isUpdatedFromDefault([
                { key: labelColor },
                { key: labelRequiredColor },
              ])}
              id={`block-setting-${id}-label`}
              title={"Label"}
              initialOpen={false}
            >
              <ColorInput
                value={labelColor}
                onChange={(val) => {
                  setProp((props) => {
                    props.labelColor = val;
                  });
                }}
                label={"Text"}
              />
              <ColorInput
                value={labelRequiredColor}
                onChange={(val) => {
                  setProp((props) => {
                    props.labelRequiredColor = val;
                  });
                }}
                label={"Required"}
              />
            </Panel>
          )}
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: containerBackgroundColor },
            ])}
            id={`block-setting-${id}-container`}
            title={"Container"}
            initialOpen={false}
          >
            <ColorInput
              value={containerBackgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.containerBackgroundColor = val;
                });
              }}
              label={"Background"}
            />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: inputMarginTablet },
            { key: inputMarginMobile },
            { key: inputMargin },
            { key: inputPaddingTablet },
            { key: inputPaddingMobile },
            { key: inputPadding },
          ])}
          id={`block-setting-${id}-input-layout`}
          title={"Input Layout"}
          initialOpen={false}
        >
          <MarginInput
            label={"Margin"}
            isResponsive={true}
            valueTablet={inputMarginTablet}
            valueMobile={inputMarginMobile}
            value={inputMargin}
            onChange={(val) => {
              setProp((props) => {
                props.inputMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.inputMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.inputMarginMobile = val;
              });
            }}
          />
          <PaddingInput
            label={"Padding"}
            isResponsive={true}
            valueTablet={inputPaddingTablet}
            valueMobile={inputPaddingMobile}
            value={inputPadding}
            onChange={(val) => {
              setProp((props) => {
                props.inputPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.inputPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.inputPaddingMobile = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMargin },
            { key: layoutMarginTablet },
            { key: layoutMarginMobile },
            { key: layoutPaddingMobile },
            { key: layoutPaddingTablet },
            { key: layoutPadding },
          ])}
          id={`block-setting-${id}-container-layout`}
          title={"Container Layout"}
          initialOpen={false}
        >
          <MarginInput
            label={"Margin"}
            isResponsive={true}
            valueTablet={layoutMarginTablet}
            valueMobile={layoutMarginMobile}
            value={layoutMargin}
            onChange={(val) => {
              setProp((props) => {
                props.layoutMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutMarginMobile = val;
              });
            }}
          />
          <PaddingInput
            label={"Padding"}
            isResponsive={true}
            valueTablet={layoutPaddingTablet}
            valueMobile={layoutPaddingMobile}
            value={layoutPadding}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutPaddingMobile = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: inputBorder },
            { key: inputBorderRadius },
            { key: inputBoxShadow },
          ])}
          id={`block-setting-${id}-input-border`}
          title={"Input Border"}
          initialOpen={false}
        >
          <BorderInput
            value={inputBorder}
            onChange={(val) => {
              setProp((props) => {
                props.inputBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={inputBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.inputBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>

        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutBorder },
            { key: layoutBorderRadius },
          ])}
          id={`block-setting-${id}-container-border`}
          title={"Container Border"}
          initialOpen={false}
        >
          <BorderInput
            value={layoutBorder}
            onChange={(val) => {
              setProp((props) => {
                props.layoutBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={layoutBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.layoutBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: inputBoxShadow }])}
          id={`block-setting-${id}-input-boxshadow`}
          title={"Input Shadows"}
          initialOpen={false}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={inputBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.inputBoxShadow = val;
              });
            }}
          />
        </Panel>

        <Panel
          isUpdated={isUpdatedFromDefault([{ key: layoutBoxShadow }])}
          id={`block-setting-${id}-container-shadows`}
          title={"Container Shaodws"}
          initialOpen={false}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={layoutBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.layoutBoxShadow = val;
              });
            }}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: name },
        { key: pattern },
        { key: required },
        { key: min },
        { key: max },
        { key: step },
        { key: value },
        { key: width },
        { key: height },
        { key: typoSize },
        { key: alignTablet },
        { key: alignMobile },
        { key: align },
        { key: showLabel },
        { key: label },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: typoColor },
        { key: typoBackgroundColor },
        { key: labelColor },
        { key: labelRequiredColor },
        { key: containerBackgroundColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: inputMarginTablet },
        { key: inputMarginMobile },
        { key: inputMargin },
        { key: inputPaddingTablet },
        { key: inputPaddingMobile },
        { key: inputPadding },
        { key: inputBorder },
        { key: inputBorderRadius },
        { key: inputBoxShadow },
        { key: layoutMargin },
        { key: layoutMarginTablet },
        { key: layoutMarginMobile },
        { key: layoutPaddingMobile },
        { key: layoutPaddingTablet },
        { key: layoutPadding },
        { key: layoutBorder },
        { key: layoutBorderRadius },
        { key: layoutBoxShadow },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
